import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

type Lang = 'zh' | 'en'
type Translations = Record<string, string>

const translations: Record<Lang, Translations> = {
  zh: {
    'profile.manage': '管理', 'profile.tags': '标签管理', 'profile.templates': '快捷模板',
    'profile.nickname': '修改昵称', 'profile.gender': '性别设置', 'profile.data': '数据',
    'profile.export': '导出数据', 'profile.import': '导入数据', 'profile.settings': '设置',
    'profile.reminder': '每日提醒', 'profile.period': '生理期记录', 'profile.spaces': '情侣/团队空间',
    'profile.vault': '设置私密密码', 'profile.logout': '退出登录',
    'cal.month': '月', 'cal.week': '周', 'cal.today': '今天',
    'stats.title': '数据统计', 'stats.total': '总记录', 'stats.activeDays': '活跃天数',
    'stats.streak': '连续打卡', 'stats.duration': '总时长',
    'stats.monthlyTrend': '📈 月度趋势', 'stats.tagDist': '🏷️ 标签分布', 'stats.tagRank': '📊 标签排行',
    'timeline.title': '时间线', 'timeline.search': '搜索活动...', 'timeline.all': '全部', 'timeline.empty': '暂无记录',
    'report.title': '📊 月度报告', 'report.total': '总记录', 'report.vsLast': '较上月',
    'report.activeDays': '活跃天数', 'report.duration': '累计时长', 'report.tagDist': '🏷️ 标签分布',
    'modal.addActivity': '记录活动', 'modal.editActivity': '编辑活动',
    'modal.time': '时间', 'modal.duration': '时长（分钟，选填）', 'modal.note': '备注（选填）',
    'modal.save': '记 录', 'modal.delete': '🗑️ 删除', 'modal.cancel': '取消',
    'modal.newTag': '＋ 新标签', 'modal.tagManage': '标签管理', 'modal.addTag': '添加标签',
    'modal.tpl': '快捷模板', 'modal.tplHint': '点击日历下方的模板可一键记录',
    'modal.addTpl': '添加模板', 'modal.nick': '修改昵称', 'modal.gender': '性别设置',
    'modal.saveBtn': '保 存', 'modal.createSpace': '创建空间', 'modal.joinSpace': '加入空间',
    'modal.members': '成员管理',
    'vault.title': '私密空间', 'vault.enter': '进入', 'vault.exit': '退出',
    'vault.inputPin': '输入私密密码', 'vault.setupPin': '设置私密密码',
    'period.title': '生理期', 'period.predict': '预测', 'period.stats': '周期统计',
    'period.records': '生理期记录', 'period.addRecord': '+ 记录生理期',
    'spaces.title': '💕 我的空间', 'spaces.create': '创建空间', 'spaces.join': '加入空间',
    'spaces.empty': '还没有空间', 'spaces.emptyHint': '创建一个或通过邀请码加入',
    'notif.enable': '已开启每日提醒', 'notif.disable': '已关闭每日提醒',
    'backup.remind': '距离上次备份已超过7天，建议导出数据', 'backup.success': '数据已导出',
    'toast.recorded': '已记录', 'toast.saved': '已保存', 'toast.deleted': '已删除',
    'confirm.delete': '确定删除？', 'confirm.deleteRecord': '删除这条记录？',
    'err.network': '网络错误', 'err.saveFailed': '保存失败', 'err.selectTag': '请选择标签',
    'err.selectDate': '请选择日期',
  },
  en: {
    'profile.manage': 'Manage', 'profile.tags': 'Tags', 'profile.templates': 'Templates',
    'profile.nickname': 'Edit Nickname', 'profile.gender': 'Gender', 'profile.data': 'Data',
    'profile.export': 'Export', 'profile.import': 'Import', 'profile.settings': 'Settings',
    'profile.reminder': 'Daily Reminder', 'profile.period': 'Period Tracker', 'profile.spaces': 'Spaces',
    'profile.vault': 'Set Vault PIN', 'profile.logout': 'Log Out',
    'cal.month': 'M', 'cal.week': 'W', 'cal.today': 'Today',
    'stats.title': 'Statistics', 'stats.total': 'Records', 'stats.activeDays': 'Active Days',
    'stats.streak': 'Streak', 'stats.duration': 'Duration',
    'stats.monthlyTrend': '📈 Monthly Trend', 'stats.tagDist': '🏷️ Tag Distribution', 'stats.tagRank': '📊 Tag Ranking',
    'timeline.title': 'Timeline', 'timeline.search': 'Search...', 'timeline.all': 'All', 'timeline.empty': 'No records',
    'report.title': '📊 Monthly Report', 'report.total': 'Total', 'report.vsLast': 'vs Last Month',
    'report.activeDays': 'Active Days', 'report.duration': 'Duration', 'report.tagDist': '🏷️ Tag Distribution',
    'modal.addActivity': 'Add Activity', 'modal.editActivity': 'Edit Activity',
    'modal.time': 'Time', 'modal.duration': 'Duration (min)', 'modal.note': 'Notes',
    'modal.save': 'Save', 'modal.delete': '🗑️ Delete', 'modal.cancel': 'Cancel',
    'modal.newTag': '＋ New Tag', 'modal.tagManage': 'Tag Manager', 'modal.addTag': 'Add Tag',
    'modal.tpl': 'Templates', 'modal.tplHint': 'Tap a template to quick-record',
    'modal.addTpl': 'Add Template', 'modal.nick': 'Edit Nickname', 'modal.gender': 'Gender',
    'modal.saveBtn': 'Save', 'modal.createSpace': 'Create Space', 'modal.joinSpace': 'Join Space',
    'modal.members': 'Members',
    'vault.title': 'Vault', 'vault.enter': 'Enter', 'vault.exit': 'Exit',
    'vault.inputPin': 'Enter PIN', 'vault.setupPin': 'Set Vault PIN',
    'period.title': 'Period', 'period.predict': 'Prediction', 'period.stats': 'Cycle Stats',
    'period.records': 'Records', 'period.addRecord': '+ Add Record',
    'spaces.title': '💕 My Spaces', 'spaces.create': 'Create', 'spaces.join': 'Join',
    'spaces.empty': 'No spaces yet', 'spaces.emptyHint': 'Create one or join with invite code',
    'notif.enable': 'Daily reminder enabled', 'notif.disable': 'Daily reminder disabled',
    'backup.remind': 'Last backup was 7+ days ago', 'backup.success': 'Data exported',
    'toast.recorded': 'Recorded', 'toast.saved': 'Saved', 'toast.deleted': 'Deleted',
    'confirm.delete': 'Delete?', 'confirm.deleteRecord': 'Delete this record?',
    'err.network': 'Network error', 'err.saveFailed': 'Save failed', 'err.selectTag': 'Select a tag',
    'err.selectDate': 'Select a date',
  },
}

interface I18nContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  t: (key: string) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    return (localStorage.getItem('cal_lang') as Lang) || 'zh'
  })

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    localStorage.setItem('cal_lang', l)
  }, [])

  const t = useCallback((key: string) => {
    return (translations[lang] || translations.zh)[key] || key
  }, [lang])

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}
